import { supabase } from './supabase';

export interface CreditTransaction {
  type: 'subscription' | 'referral' | 'monthly' | 'consumption' | 'bonus';
  amount: number;
  description?: string;
  examType?: 'mcq' | 'theory' | 'adaptive' | 'pdf';
  referenceId?: string;
}

export class CreditService {
  static async getUserCredits(userId: string): Promise<number> {
    const { data, error } = await supabase
      .from('profiles')
      .select('credits')
      .eq('id', userId)
      .single();

    if (error) throw error;
    return data?.credits || 0;
  }

  static async addCredits(
    userId: string, 
    amount: number, 
    transaction: Omit<CreditTransaction, 'amount'>
  ): Promise<{ newBalance: number; transactionId: string }> {
    const currentBalance = await this.getUserCredits(userId);
    const newBalance = currentBalance + amount;

    const { error: updateError } = await supabase
      .from('profiles')
      .update({ credits: newBalance })
      .eq('id', userId);

    if (updateError) throw updateError;

    const { data: logData, error: logError } = await supabase
      .from('credit_logs')
      .insert({
        user_id: userId,
        type: transaction.type,
        amount: amount,
        balance_before: currentBalance,
        balance_after: newBalance,
        description: transaction.description,
        exam_type: transaction.examType,
        reference_id: transaction.referenceId
      })
      .select('id')
      .single();

    if (logError) throw logError;

    return {
      newBalance,
      transactionId: logData.id
    };
  }

  static async consumeCredits(
    userId: string, 
    examType: 'mcq' | 'theory' | 'adaptive' | 'pdf',
    examDescription?: string
  ): Promise<{ success: boolean; remainingCredits: number; transactionId?: string }> {
    try {
      const currentBalance = await this.getUserCredits(userId);

      if (currentBalance < 1) {
        return {
          success: false,
          remainingCredits: currentBalance
        };
      }

      const result = await this.addCredits(userId, -1, {
        type: 'consumption',
        description: examDescription || `Generated ${examType.toUpperCase()} exam`,
        examType,
      });

      return {
        success: true,
        remainingCredits: result.newBalance,
        transactionId: result.transactionId
      };
    } catch (error) {
      console.error('Error consuming credits:', error);
      throw error;
    }
  }

  static async getCreditHistory(
    userId: string, 
    limit = 50, 
    offset = 0
  ): Promise<any[]> {
    const { data, error } = await supabase
      .from('credit_logs')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) throw error;
    return data || [];
  }

  static async canGenerateExam(userId: string): Promise<{ canGenerate: boolean; credits: number }> {
    const credits = await this.getUserCredits(userId);
    return {
      canGenerate: credits >= 1,
      credits
    };
  }
}